import { useState } from "react";
import { ThumbsUp, ThumbsDown } from "lucide-react";
import { downloadInvoice } from "src/services/invoice-api";
import { Button, Modal } from "src/components";
import { useToast } from "src/hooks/useToast";
import InvoiceEscalationAction from "./InvoiceEscalationAction";

type AgentRating = "UP" | "DOWN" | "";

const InvoiceApprovalDetails = ({
    row,
    resetPagination,
    onRefresh,
    company_code
}: {
    row: any;
    resetPagination: () => void;
    onRefresh: () => void;
    company_code: any;
}) => {
    const data = row?.original;
    const [invoiceAction, setInvoiceAction] = useState<string>("");
    const [agentRating, setAgentRating] = useState<AgentRating>("");
    const { toast } = useToast();

    const invoice = data;

    const Company = company_code.some((item: any) => item?.code === 618) ? 618 : 0; 
    const renderRow = (label: string, value: React.ReactNode | (() => React.ReactNode)) => (
        <tr className="text-sm text-[#303030]">
            <td className="p-3 border border-[#E3E3E3] bg-[#f7f7f7] font-medium">{label}</td>
            <td className="p-3 border border-[#E3E3E3]">
                {typeof value === "function" ? value() : value ?? "-"}
            </td>
        </tr>
    );

    return (
        <section className="px-5 pb-5 border border-[#ccc] border-t-0 rounded-b-lg">
            <table className="w-full border-collapse">
                <tbody>
                    {renderRow("Invoice Number", invoice?.invoice_number)}
                    {renderRow("FUID", invoice?.fuid)}
                    {renderRow("Status", invoice?.status)}
                     {renderRow("Responded Remarks", invoice?.responded_reason)}
                    {renderRow("Pharmacist code", <span>
                        <a
                            className="text-gray-900 hover:text-[#6b40d6] w-fit"
                            href={`/masters/counters/details/?counter_code=${invoice?.pharmacist_code}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => {
                                // window.localStorage.setItem("counterCompanyLocationId", invoiceData?.counter_company_location_id);
                                // window.localStorage.setItem("company_code",invoiceData?.company_code);
                            }}
                        >
                            {invoice?.pharmacist_code}
                        </a>
                    </span>)}
                    {renderRow("Escalated Date", invoice?.escalated_date
                        ? `${new Date(invoice.escalated_date).toLocaleDateString("en-GB", {
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                        })}, ${new Date(invoice.escalated_date).toLocaleTimeString("en-GB", {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                        })}`
                        : "-"
                    )}
                    {renderRow("Escalated By", invoice?.escalated_by)}

                    {renderRow("Entry Type", invoice?.entry_type)}

                    {renderRow("Link Type", invoice?.link_type)}
                    {renderRow("Remarks Type", invoice?.remark_type)}
                    {renderRow("Escalated Remarks", invoice?.comments)}
                    {renderRow("Invoice", (
                        <Button onClick={() => {
                            if (!invoice?.fuid_path) {
                                toast({ description: "Invoice not found", variant: "destructive" });
                                return;
                            }
                            downloadInvoice(invoice?.fuid_path, Company).then((res: any) => {
                                if (res?.data?.success && res?.data?.data) {
                                    window.open(res?.data?.data, "_blank"); // Opens the link in a new tab
                                } else {
                                    console.error("Failed to download the invoice.");
                                }
                            });
                        }} className="text-black  bg-[#F1F1F1] hover:bg-[#f6f6f6] px-3 py-1 rounded text-sm">
                            Download
                        </Button>
                    ))}
                    {renderRow("Agent Remarks", invoice?.agentsuggestion_remarks)}
                    {renderRow("Agent Rating", (
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                aria-label="Thumbs up"
                                aria-pressed={agentRating === "UP"}
                                onClick={() => setAgentRating(agentRating === "UP" ? "" : "UP")}
                                className={`p-2 rounded border transition ${agentRating === "UP" ? "bg-[#8051FF] text-white border-[#8051FF]" : "bg-white text-[#303030] border-[#D0D0D0] hover:bg-[#F1F1F1]"}`}
                            >
                                <ThumbsUp className="w-4 h-4" />
                            </button>
                            <button
                                type="button"
                                aria-label="Thumbs down"
                                aria-pressed={agentRating === "DOWN"}
                                onClick={() => setAgentRating(agentRating === "DOWN" ? "" : "DOWN")}
                                className={`p-2 rounded border transition ${agentRating === "DOWN" ? "bg-[#8051FF] text-white border-[#8051FF]" : "bg-white text-[#303030] border-[#D0D0D0] hover:bg-[#F1F1F1]"}`}
                            >
                                <ThumbsDown className="w-4 h-4" />
                            </button>
                        </div>
                    ))}

                </tbody>
            </table>
            {(invoice?.status === "pending" || invoice?.status === "on hold") && (
                <footer className="mt-5 flex items-center justify-end gap-3">
                    {invoice?.status === "pending" && (
                        <Button
                            onClick={() => setInvoiceAction("on hold")}
                            className="w-fit h-9 mt-0 font-normal border border-[#D0D0D0] bg-[#F1F1F1] shadow-sm"
                        >
                            On Hold
                        </Button>
                    )}
                    <Button
                        onClick={() => setInvoiceAction("rejected")}
                        className="w-fit h-9 mt-0 font-normal border border-[#D0D0D0] bg-[#F1F1F1] shadow-sm"
                    >
                        Reject
                    </Button>
                    <Button
                        onClick={() => setInvoiceAction("resolved")}
                        className="text-white bg-[#8051FF]"
                    >
                        Accept
                    </Button>
                </footer>
            )}

            <Modal isOpen={invoiceAction === "resolved" || invoiceAction === "rejected" || invoiceAction === "on hold"} onCloseModal={() => setInvoiceAction("")} isHeader={false}>
                <InvoiceEscalationAction
                    onSuccess={() => {
                        setInvoiceAction("");
                        onRefresh();
                        resetPagination()
                    }}
                    action={invoiceAction}
                    onCloseModal={() => setInvoiceAction("")}
                    invoiceData={invoice}
                    company_code={company_code}
                    agentsuggestion_rating={agentRating}
                />
            </Modal>
        </section>
    );
};

export default InvoiceApprovalDetails;
